/**
 * sanitize-signature-html — client-side sanitiser for the rich e-mail-signature editor
 * (B3 rich upgrade).
 *
 * This mirrors the SERVER allowlist (`apps/api/.../sanitize-signature-html.ts`) so the
 * live "bereinigt" preview shows the broker roughly what will actually be stored and sent.
 * It is NOT the security boundary — the SERVER re-sanitises every signature at write time,
 * and that is authoritative. This is a UX convenience: render the editor's contentEditable
 * output safely and honestly before it is saved.
 *
 * Reuses the DOMPurify dependency already vendored for the inbound-mail sanitiser — no new
 * dependency. The allowlist is deliberately the same minute set as the server:
 *   tags  b strong i em u a br p div span img
 *   attrs a[href] · img[src|alt|width|height] · span/p/div[style]
 * Data-URI images are allowed on <img> ONLY (the logo path); the server enforces the raster
 * MIME allowlist + the 256 KB byte cap, which is the check that actually matters.
 */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'a', 'br', 'p', 'div', 'span', 'img'];
const ALLOWED_ATTR = ['href', 'src', 'alt', 'width', 'height', 'style'];

/** Sanitise rich-signature HTML for the live preview. Href limited to http/https/mailto. */
export function sanitizeSignatureHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Anchors never open a new tab from a signature preview, and target is not on the
    // server allowlist — drop it so the preview matches what gets stored.
    FORBID_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
    // Inline logo: a data: URI is valid on <img> only. The server restricts this to raster
    // MIME types within a 256 KB cap; the editor also rejects oversized files up front.
    ADD_DATA_URI_TAGS: ['img'],
    ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
  });
}
